import { isAndroid, isWeb } from "@memorang/helpers/platform";
import { StyleSheet } from "react-native";
import type { AppTheme } from "../../useAppTheme";

export type TextAlign =
	| "left"
	| "auto"
	| "right"
	| "center"
	| "justify"
	| undefined;

export const normalizeFontSize = (original: number, custom: number) => {
	const defaultFontSize = 14;
	return (original * custom) / defaultFontSize;
};

export const getStyles = (
	theme: AppTheme,
	fontSize = 14,
	textAlign: TextAlign = "left",
	customStyles: Record<string, unknown> = {},
	appFont = "opensans",
) => {
	const styles = StyleSheet.create({
		headingContainer: {
			flexDirection: "row",
			marginTop: 0,
			marginBottom: 0,
		},
		heading1Container: {
			paddingBottom: 0,
			borderBottomWidth: 0,
		},
		heading2Container: {
			paddingBottom: 0,
			borderBottomWidth: 0,
		},
		heading1: {
			fontSize: normalizeFontSize(32, fontSize),
			lineHeight: normalizeFontSize(32, fontSize) * 1.5,
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
			fontWeight: "500",
		},
		heading2: {
			fontSize: normalizeFontSize(24, fontSize),
			lineHeight: normalizeFontSize(24, fontSize) * 1.5,
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
			fontWeight: "500",
		},
		heading3: {
			fontSize: normalizeFontSize(18, fontSize),
			lineHeight: normalizeFontSize(18, fontSize) * 1.5,
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
			fontWeight: "500",
		},
		heading4: {
			fontSize: normalizeFontSize(16, fontSize),
			lineHeight: normalizeFontSize(16, fontSize) * 1.5,
			fontWeight: "500",
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
		},
		heading5: {
			fontSize: normalizeFontSize(13, fontSize),
			lineHeight: normalizeFontSize(13, fontSize) * 1.5,
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
		},
		heading6: {
			fontSize: normalizeFontSize(11, fontSize),
			lineHeight: normalizeFontSize(11, fontSize) * 1.5,
			marginVertical: normalizeFontSize(32, fontSize) * 0.2,
		},
		hr: {
			backgroundColor: theme.palette.divider,
			marginVertical: 8,
		},
		blockquote: {
			backgroundColor: theme.colors.background,
			borderColor: theme.palette.highlight.quote,
			paddingVertical: 8,
			marginLeft: 0,
			paddingLeft: 8,
		},
		bullet_list: {
			fontSize,
			letterSpacing: 0.1,
			lineHeight: fontSize * 1.5,
			marginBottom: fontSize * 0.5,
			color: theme.colors.text,
		},
		ordered_list: {
			fontSize,
			letterSpacing: 0.1,
			lineHeight: fontSize * 1.5,
			color: theme.colors.text,
			fontFamily: `${appFont}-regular`,
		},
		list_item: {
			marginTop: fontSize * 0.2,
			lineHeight: isWeb ? fontSize * 1 : fontSize * 2.2,
			fontSize,
		},
		bullet_list_icon: {
			fontSize: isWeb ? fontSize * 1.5 : isAndroid ? fontSize : 44,
			width: 10,
			marginTop: isAndroid ? -6 : 0,
			height: fontSize * 1.5,
			alignSelf: "flex-start",
			marginLeft: 4,
		},
		ordered_list_icon: {
			fontSize,
			lineHeight: fontSize * 1.5,
			marginLeft: 4,
			marginRight: 8,
		},
		code_inline: {
			borderWidth: 1,
			borderColor: theme.palette.divider,
			backgroundColor: theme.colors.surfaceVariant,
			padding: 8,
			lineHeight: fontSize * 1.5,
			color: theme.palette.text.primary,
		},
		code_block: {
			borderColor: theme.palette.divider,
			backgroundColor: theme.colors.background,
			color: theme.palette.text.primary,
			fontSize,
		},
		fence: {
			borderColor: theme.palette.divider,
			backgroundColor: theme.colors.background,
			color: theme.palette.text.primary,
			lineHeight: fontSize * 1.3,
			fontSize,
		},
		table: {
			borderWidth: 1,
			borderColor: theme.palette.divider,
			borderRadius: 4,
			marginBottom: 16,
			overflow: "hidden",
		},
		tableHeader: {
			backgroundColor: theme.colors.surface,
		},
		tableHeaderCell: {
			borderWidth: 0,
			paddingVertical: 6,
			paddingHorizontal: 8,
			fontWeight: "600",
		},
		tableRow: {
			borderBottomWidth: 1,
			borderColor: theme.palette.divider,
			flexDirection: "row",
		},
		tableRowCell: {
			borderWidth: 0,
			paddingVertical: 6,
			paddingHorizontal: 8,
		},
		link: {
			textDecorationLine: "underline",
			color: theme.colors.secondary,
		},
		blocklink: {
			borderColor: theme.palette.divider,
		},
		text: {
			textAlign,
			color: theme.palette.text.primary,
		},
		textgroup: {
			textAlign,
		},
		strong: {
			fontWeight: "bold",
		},
		em: {
			fontStyle: "italic",
		},
		s: {
			textDecorationLine: "line-through",
		},
		sup: {
			fontSize: 0.7 * fontSize,
			lineHeight: 0.7 * fontSize,
			...(isWeb
				? {
						transform: [{ translateY: -fontSize * 0.35 }],
						display: "inline-block" as "flex",
					}
				: {}),
		},
		sub: {
			fontSize: 0.7 * fontSize,
			lineHeight: 0.7 * fontSize,
			...(isWeb
				? {
						transform: [{ translateY: fontSize * 0.2 }],
						display: "inline-block" as "flex",
					}
				: {}),
		},
	});

	// biome-ignore lint/suspicious/noExplicitAny: merging across RMR style keys from mixed Text/View style types
	const writable = styles as Record<string, any>;
	for (const key in customStyles) {
		const customStyle = customStyles[key];
		if (!customStyle) {
			continue;
		}
		writable[key] = {
			...(writable[key] ?? {}),
			...customStyle,
		};
	}

	return styles;
};

export type Styles = ReturnType<typeof getStyles>;
// Intentionally loose: RMR exposes many render-rule-specific style keys
// (paragraph, image, code_inline, …) and web consumers occasionally pass
// platform-specific CSS props that aren't in React Native's Text/ViewStyle.
// biome-ignore lint/suspicious/noExplicitAny: see note above
export type CustomStyle = Record<string, any>;
